import { useEffect, useRef } from 'react';

const useMorphingBackground = (photos, setBackground) => {
  const indexRef = useRef(0);
  const lastBackgroundRef = useRef(null); // Preserve last background across filter changes

  // Change this in CSS as well!
  const fadeDuration = 20_000;
  const intervalDuration = 30_000;

  useEffect(() => {
    if (!photos?.length) return;

    const blurImages = photos
      .filter(p => p.image_blur)
      .map(p => p.image_blur);

    if (!blurImages.length) return;

    // Pick first image from new set
    const firstImage = blurImages[0];
    
    // If we have a previous background, keep it while loading the new one
    if (lastBackgroundRef.current && lastBackgroundRef.current !== firstImage) {
      // Preload the new first image
      const img = new Image();
      img.src = firstImage;
      
      img.onload = () => {
        // Fade from old background to new
        setBackground({
          current: lastBackgroundRef.current,
          next: firstImage,
          showNext: false,
        });
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setBackground(prev => ({
              ...prev,
              showNext: true,
            }));
          });
        });
        
        setTimeout(() => {
          setBackground({
            current: firstImage,
            next: null,
            showNext: false,
          });
          lastBackgroundRef.current = firstImage;
        }, fadeDuration);
      };
    } else {
      // No previous background, just set immediately
      setBackground({
        current: firstImage,
        next: null,
        showNext: false,
      });
      lastBackgroundRef.current = firstImage;
    }

    const interval = setInterval(() => {
      indexRef.current = (indexRef.current + 1) % blurImages.length;
      const url = blurImages[indexRef.current];

      // Preload next image
      const img = new Image();
      img.src = url;

      img.onload = () => {
        // 1️⃣ Mount next image at opacity 0
        setBackground(prev => ({
          ...prev,
          next: url,
          showNext: false,
        }));
      
        // 2️⃣ Force browser to paint once
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // 3️⃣ Trigger fade-in
            setBackground(prev => ({
              ...prev,
              showNext: true,
            }));
          });
        });
      
        // 4️⃣ Commit after fade completes
        setTimeout(() => {
          setBackground({
            current: url,
            next: null,
            showNext: false,
          });
          lastBackgroundRef.current = url;
        }, fadeDuration);
      };

    }, intervalDuration);

    return () => clearInterval(interval);
  }, [photos, setBackground, fadeDuration]);
};

export default useMorphingBackground;
